import styled from "@emotion/styled";
import { Autocomplete, Button, Checkbox, Menu, Switch, Table, Tabs, TextField, Tooltip, tooltipClasses } from "@mui/material";
import ReactQuill from "react-quill";


const primary_color = "var(--primary_color)";
const secondary_color = "var(--secondary_color)";
const def_in_padding = "var(--input_padding)";

export const CTextField = styled(TextField)(({ fontSize = "1rem", input_padding = def_in_padding, ...props }) => {
  return ({
    margin: props.input_margin ?? "0.2rem 0",
    height: props?.maxRows > 1 ? 'auto' : 'fit-content',
    "& .MuiInputBase-input": {
      padding: `${input_padding}`,
      '::placeholder': { textTransform: 'capitalize' },
      '::-ms-input-placeholder': { textTransform: 'capitalize' },
    },
    "& .MuiInputBase-multiline": { padding: props.multiline ? input_padding : 0, textarea: { padding: 0 } },
    "& .MuiInputLabel-root": { textTransform: props.textTrans ?? "capitalize" },
    "& .MuiInputLabel-root:not(.Mui-error).Mui-focused:not(.Mui-error)": {
      color: primary_color,
      fontWeight: "600",
    },
    "& .MuiInputLabel-root:not(Mui-error, .Mui-focused, .MuiInputLabel-shrink)": {
      transform: `translate(${fontSize}, calc(${fontSize} / 2 )) scale(1)`,
    },
    "& .MuiInputBase-adornedEnd": { padding: 0, fontSize: fontSize },
    "& .MuiIcon-root": { fontSize: `calc(${fontSize} + 0.4rem)`, padding: 0 },
    "& .MuiOutlinedInput-root:not(.Mui-error, .Mui-disabled )": {
      "&.Mui-focused > fieldset": { borderColor: primary_color },
      "&:hover > fieldset": { borderColor: primary_color },
    },
  })
});

export const CButton = styled(Button)(({ btn_padding, ...props }) => {
  const text_flag = ["text", "outlined"].includes(props.variant);

  const color_def = text_flag ? primary_color : "white";
  const bg_color_def = text_flag ? "transparent" : props.bg_color ?? primary_color;
  const disabled_def = { color: "white", borderColor: "grey", backgroundColor: "grey", cursor: 'not-allowed' }

  return {
    textTransform: props.textTrans ?? "capitalize",
    fontWeight: text_flag ? 600 : "normal",
    padding: btn_padding ?? "0.4rem 0.5rem",
    color: props.txt_color ?? color_def,
    borderColor: color_def,
    backgroundColor: props.bg_color ?? bg_color_def,
    borderRadius: '0.5rem',
    gap: '0.3rem',
    alignItems: 'center',
    '& .MuiButton-startIcon': { margin: 0 },
    "&:disabled": { ...disabled_def, ...props.disabled_style },
    "&:hover:not(.Mui-disabled)": {
      backgroundColor: props.bg_hover_color ?? bg_color_def,
      borderColor: color_def,
    },
  };
});

export const CTooltip = styled(({ className, ...props }) => <Tooltip {...props} classes={{ popper: className }} />)(
  () => ({
    [`& .${tooltipClasses.tooltip}`]: {
      textTransform: "capitalize",
    },
  })
);

export const CTabs = styled(Tabs)(() => {
  const border_radius = "0.3rem";
  return {
    minHeight: "1rem",
    padding: "0.5rem",
    marginBottom: "0.5rem",
    boxShadow: "0 1px 2px var(--shadow_color)",
    borderRadius: border_radius,
    "& .MuiTab-textColorPrimary.Mui-selected": { color: "white" },
    "& .MuiButtonBase-root.MuiTab-root": {
      textTransform: "capitalize",
      fontWeight: "bold",
      fontSize: "1rem",
      padding: "0.3rem 1rem",
      minHeight: "1rem",
    },
    "& .Mui-selected": { backgroundColor: primary_color, borderRadius: border_radius },
    "& .MuiTabs-indicator": { display: "none" },
  };
});

export const CAutocomplete = styled(Autocomplete)(({ fontSize = "1rem" }) => ({
  "& .MuiOutlinedInput-root": { padding: "0", button: { margin: 0 } },
  "& .MuiAutocomplete-input ": { padding: "0.4rem 0.5rem !important" },
  "& .MuiAutocomplete-endAdornment": { margin: 0, padding: 0 },
}));

export const CAutocompleteMultiple = styled(Autocomplete)(() => ({
  width: '100%',
  lineHeight: 'normal',
  "& .MuiInputBase-root": {
    gap: '0.2rem',
    padding: '0.5rem !important',
    // paddingTop: '0.2rem !important',
  },
  "& .MuiInputBase-input": { paddingBlock: '0 !important' },
  "& .MuiAutocomplete-endAdornment": { margin: 0, padding: 0 },
  "& .MuiChip-root": { height: '24px', borderRadius: '0.5rem', margin: 0 },
}))

export const CCheckbox = styled(Checkbox)(() => ({
  padding: "0",
}));

export const CTable = styled(Table)(({ }) => {
  const cellpadding = '0.3rem 0.5rem';
  return {
    width: '100%',
    marginBottom: '0.5rem',
    th: { whiteSpace: 'nowrap', textAlign: 'center', fontWeight: '600', padding: cellpadding },
    td: { padding: cellpadding }
  }
})

export const CSwitch = styled(Switch)(({ }) => ({
  '& .MuiButtonBase-root.Mui-checked': { color: primary_color },
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: secondary_color }
}))

export const CEditor = styled(ReactQuill)(({ mh = '200px', border_color = '' }) => {
  return {
    '& .ql-toolbar': { borderRadius: '0.5rem 0.5rem 0 0', borderColor: `${border_color} !important` },
    '& .ql-formats': {
      borderRight: '1px solid lightgrey', marginRight: `0 !important`, padding: '0 5px',
      '&:last-child': { border: 'none' }
    },
    '& .ql-container': {
      borderRadius: '0 0 0.5rem 0.5rem',
      maxHeight: mh,
      overflow: 'auto',
      borderColor: `${border_color} !important`
    },
  }
})

export const CMenu = styled(Menu)(() => {
  return {
    '& .MuiPaper-root ': {
      marginTop: '0.2rem',
      border: '1px solid hsla(0,0%,0%,0.5)',
      boxShadow: "2px 2px 2px hsla(0,0%,0%,0.5)",
    }
  }
})